import i18n from '@/i18n'

export function targetForProfile(manifest, profileId, configuration) {
  const profile = manifest?.assetProfiles?.[profileId]
  if (!profile) return null
  const target = profile.target
  if (target === 'selected-format' || target?.type === 'selected-format') {
    const format = (manifest.formats || []).find(
      (entry) => entry.id === configuration?.format?.id,
    )
    if (!format) return null
    return {
      ...profile,
      profileId,
      width: Number(format.width),
      height: Number(format.height),
    }
  }
  if (target?.type === 'fixed') {
    return {
      ...profile,
      profileId,
      width: Number(target.width),
      height: Number(target.height),
    }
  }
  if (target?.type === 'matrix') {
    const key = (target.selectors || [])
      .map((path) => String(path).split('.').reduce((cursor, part) => cursor?.[part], configuration))
      .join('|')
    const dimensions = target.sizes?.[key] || target.fallback
    if (!dimensions) return null
    return {
      ...profile,
      profileId,
      width: Number(dimensions.width),
      height: Number(dimensions.height),
    }
  }
  return {
    ...profile,
    profileId,
    width: Number(profile.width),
    height: Number(profile.height),
  }
}

// Normalize browser crop coordinates before they enter configuration JSON.
export function normalizeCrop(coordinates, image) {
  if (!coordinates || !image?.width || !image?.height) return null
  const round = (value) => Math.round(value * 10000) / 10000
  return {
    x: round(coordinates.left / image.width * 100),
    y: round(coordinates.top / image.height * 100),
    width: round(coordinates.width / image.width * 100),
    height: round(coordinates.height / image.height * 100),
  }
}

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum)
}

function roundPercentage(value) {
  return Math.round(value * 10000) / 10000
}

export function cropAspectMatches(crop, image, target, tolerance = 0.01) {
  if (!crop || !image?.width || !image?.height || !target?.width || !target?.height) {
    return false
  }
  const cropWidth = Number(crop.width) / 100 * Number(image.width)
  const cropHeight = Number(crop.height) / 100 * Number(image.height)
  if (cropWidth <= 0 || cropHeight <= 0) return false
  const targetRatio = Number(target.width) / Number(target.height)
  return Math.abs((cropWidth / cropHeight) - targetRatio) / targetRatio <= tolerance
}

export function cropForTarget(crop, image, target) {
  if (!image?.width || !image?.height || !target?.width || !target?.height) return null
  if (cropAspectMatches(crop, image, target)) {
    return {
      x: roundPercentage(Number(crop.x)),
      y: roundPercentage(Number(crop.y)),
      width: roundPercentage(Number(crop.width)),
      height: roundPercentage(Number(crop.height)),
    }
  }

  const sourceWidth = Number(image.width)
  const sourceHeight = Number(image.height)
  const sourceRatio = sourceWidth / sourceHeight
  const targetRatio = Number(target.width) / Number(target.height)
  const focusX = crop
    ? Number(crop.x || 0) + Number(crop.width || 100) / 2
    : 50
  const focusY = crop
    ? Number(crop.y || 0) + Number(crop.height || 100) / 2
    : 50
  let width = 100
  let height = 100

  if (sourceRatio > targetRatio) {
    width = targetRatio / sourceRatio * 100
  } else {
    height = sourceRatio / targetRatio * 100
  }

  return {
    x: roundPercentage(clamp(focusX - width / 2, 0, 100 - width)),
    y: roundPercentage(clamp(focusY - height / 2, 0, 100 - height)),
    width: roundPercentage(width),
    height: roundPercentage(height),
  }
}

/**
 * The target-shaped crop centred on a point of the source, in percentages.
 *
 * The ad renders the crop, never the point: this is what turns "keep the roof
 * in shot" into a box. A point too near an edge to centre pins the box to that
 * edge rather than running off the image, so the focus stays inside the cut
 * even when it cannot sit in the middle of it.
 *
 * The pipeline computes the same box for a generated ad (templates/slider/
 * focal.py). The two must agree, or reopening a generated ad and nudging the
 * focus would jump the image.
 */
export function cropAroundFocus(image, target, focalPoint, zoom = 1) {
  const base = cropForTarget(null, image, target)
  if (!base) return null
  const safeZoom = clamp(Number(zoom || 1), 1, 3)
  const width = base.width / safeZoom
  const height = base.height / safeZoom
  const focusX = clamp(Number(focalPoint?.x ?? 50), 0, 100)
  const focusY = clamp(Number(focalPoint?.y ?? 50), 0, 100)
  return {
    x: roundPercentage(clamp(focusX - width / 2, 0, 100 - width)),
    y: roundPercentage(clamp(focusY - height / 2, 0, 100 - height)),
    width: roundPercentage(width),
    height: roundPercentage(height),
  }
}

const NAMED_POSITION = { left: 0, top: 0, center: 50, right: 100, bottom: 100 }

export function cropForFocus(image, target, horizontal = 'center', vertical = 'center', zoom = 1) {
  return cropAroundFocus(
    image,
    target,
    {
      x: NAMED_POSITION[horizontal] ?? 50,
      y: NAMED_POSITION[vertical] ?? 50,
    },
    zoom,
  )
}

/**
 * Where a crop box is looking, for an image that carries no focus point.
 *
 * A crop saved before focus points existed, or one dragged by hand, still has
 * a centre; showing that is closer to the truth than showing the middle of the
 * photograph and claiming nothing was chosen.
 */
export function focusOfCrop(crop) {
  if (!crop) return null
  const x = Number(crop.x ?? 0) + Number(crop.width ?? 100) / 2
  const y = Number(crop.y ?? 0) + Number(crop.height ?? 100) / 2
  return { x: roundPercentage(clamp(x, 0, 100)), y: roundPercentage(clamp(y, 0, 100)) }
}

export function cropPixelSize(crop, image) {
  if (!crop || !image?.width || !image?.height) return null
  return {
    width: Number(crop.width) / 100 * Number(image.width),
    height: Number(crop.height) / 100 * Number(image.height),
  }
}

export function resolutionStatus(crop, image, target) {
  if (!image?.width || !image?.height || !target?.width || !target?.height) return null
  const targetWidth = Number(target.width)
  const targetHeight = Number(target.height)

  if (target.mode === 'contain' || target.mode === 'fit') {
    const standardScale = Math.min(
      targetWidth / Number(image.width),
      targetHeight / Number(image.height),
    )
    if (standardScale > 1.0001) {
      return {
        level: 'standard',
        sourceWidth: Math.floor(Number(image.width)),
        sourceHeight: Math.floor(Number(image.height)),
        requiredWidth: targetWidth,
        requiredHeight: targetHeight,
      }
    }
    if (standardScale * 2 > 1.0001) {
      return {
        level: 'retina',
        sourceWidth: Math.floor(Number(image.width)),
        sourceHeight: Math.floor(Number(image.height)),
        requiredWidth: targetWidth * 2,
        requiredHeight: targetHeight * 2,
      }
    }
    return null
  }

  const adjustedCrop = cropForTarget(crop, image, target)
  const pixels = cropPixelSize(adjustedCrop, image)
  if (!pixels) return null
  if (pixels.width + 0.5 < targetWidth || pixels.height + 0.5 < targetHeight) {
    return {
      level: 'standard',
      cropWidth: Math.floor(pixels.width),
      cropHeight: Math.floor(pixels.height),
      requiredWidth: targetWidth,
      requiredHeight: targetHeight,
    }
  }
  if (pixels.width + 0.5 < targetWidth * 2 || pixels.height + 0.5 < targetHeight * 2) {
    return {
      level: 'retina',
      cropWidth: Math.floor(pixels.width),
      cropHeight: Math.floor(pixels.height),
      requiredWidth: targetWidth * 2,
      requiredHeight: targetHeight * 2,
    }
  }
  return null
}

export function cropDialogUseful(image, target) {
  if (!image?.width || !image?.height || !target?.width || !target?.height) return false
  const sourceRatio = Number(image.width) / Number(image.height)
  const targetRatio = Number(target.width) / Number(target.height)
  const ratioDiffers = Math.abs(sourceRatio - targetRatio) / targetRatio > 0.01
  const pixels = cropPixelSize(cropForTarget(null, image, target), image)
  const hasStandardPixels = Boolean(
    pixels
    && pixels.width + 0.5 >= Number(target.width)
    && pixels.height + 0.5 >= Number(target.height),
  )
  if (!hasStandardPixels) return false
  const hasUsefulZoomRoom = (
    pixels.width >= Number(target.width) * 1.25
    && pixels.height >= Number(target.height) * 1.25
  )
  return ratioDiffers || hasUsefulZoomRoom
}

function loadImage(sourceUrl, allowProxy = true) {
  return new Promise((resolve, reject) => {
    const image = new window.Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => resolve(image)
    image.onerror = () => {
      // Website/AI sources often display normally but omit canvas CORS headers.
      // Use the existing validated same-origin importer for editable pixels.
      if (allowProxy && /^https?:\/\//i.test(sourceUrl)
        && new URL(sourceUrl).origin !== window.location.origin) {
        resolve(loadImage(`/api/studio-editor-v2/media-library/fetch?url=${encodeURIComponent(sourceUrl)}`, false))
      } else reject(new Error(i18n.t('editorV2.core.errors.imageSourceUnreadable')))
    }
    image.src = sourceUrl
  })
}

export async function readImageDimensions(source) {
  if (source instanceof File && typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(source, { imageOrientation: 'from-image' })
      const dimensions = { width: bitmap.width, height: bitmap.height }
      bitmap.close()
      return dimensions
    } catch {
      // Fall through to an object URL, which works in older browsers.
    }
  }

  let sourceUrl = source
  let revoke = false
  if (source instanceof File) {
    sourceUrl = URL.createObjectURL(source)
    revoke = true
  }
  try {
    const image = await loadImage(sourceUrl)
    return { width: image.naturalWidth, height: image.naturalHeight }
  } finally {
    if (revoke) URL.revokeObjectURL(sourceUrl)
  }
}

/**
 * The pixel density of a crop preview (AD-257). The server bakes a 1× and a 2× rendition and the
 * ad picks one with srcset; a preview drawn at 1× only was stretched on a Retina screen and looked
 * blurry until the ad was saved. Drawn at the screen's density, at most 2× like the saved ad.
 */
export function previewDensity() {
  const ratio = typeof window === 'undefined' ? 1 : Number(window.devicePixelRatio) || 1
  return Math.min(2, Math.max(1, ratio))
}

export async function createCropPreview(sourceUrl, crop, target, focalPoint) {
  const image = await loadImage(sourceUrl)
  const dimensions = { width: image.naturalWidth, height: image.naturalHeight }
  const normalized = focalPoint ? cropAroundFocus(dimensions, target, focalPoint) : cropForTarget(
    crop,
    dimensions,
    target,
  )
  if (!normalized) throw new Error(i18n.t('editorV2.core.errors.cropFailed'))

  const density = previewDensity()
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(Number(target.width) * density)
  canvas.height = Math.round(Number(target.height) * density)
  const context = canvas.getContext('2d')
  if (!context) throw new Error(i18n.t('editorV2.core.errors.previewFailed'))

  const left = Number(normalized.x) / 100 * image.naturalWidth
  const top = Number(normalized.y) / 100 * image.naturalHeight
  const width = Number(normalized.width) / 100 * image.naturalWidth
  const height = Number(normalized.height) / 100 * image.naturalHeight
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(
    image,
    left,
    top,
    width,
    height,
    0,
    0,
    canvas.width,
    canvas.height,
  )

  let previewUrl = canvas.toDataURL('image/webp', 0.82)
  if (!previewUrl.startsWith('data:image/webp')) {
    previewUrl = canvas.toDataURL('image/jpeg', 0.86)
  }
  return { crop: normalized, previewUrl }
}

export function cropCoordinates(crop, imageSize) {
  if (!crop || !imageSize?.width || !imageSize?.height) return null
  return {
    left: Number(crop.x || 0) / 100 * imageSize.width,
    top: Number(crop.y || 0) / 100 * imageSize.height,
    width: Number(crop.width || 100) / 100 * imageSize.width,
    height: Number(crop.height || 100) / 100 * imageSize.height,
  }
}

export function retinaShortfall(crop, image, target) {
  if (!crop || !image || !target) return null
  const cropWidth = Number(crop.width) / 100 * Number(image.width)
  const cropHeight = Number(crop.height) / 100 * Number(image.height)
  const requiredWidth = Number(target.width) * 2
  const requiredHeight = Number(target.height) * 2
  if (cropWidth + 0.5 >= requiredWidth && cropHeight + 0.5 >= requiredHeight) return null
  return {
    cropWidth: Math.floor(cropWidth),
    cropHeight: Math.floor(cropHeight),
    requiredWidth,
    requiredHeight,
  }
}

export function emptyMediaReference() {
  return {
    sourceAssetId: null,
    renditionAssetId: null,
    crop: null,
  }
}

/**
 * A media reference with a new focus point and the crop that follows from it.
 *
 * The rendition is dropped on purpose: it is the baked crop, so it no longer
 * matches and the field's own effect regenerates it from the new box.
 */
export function mediaWithFocus(media, image, target, focalPoint) {
  const crop = cropAroundFocus(image, target, focalPoint)
  return {
    ...media,
    focalPoint: { x: roundPercentage(focalPoint.x), y: roundPercentage(focalPoint.y) },
    crop: crop || media?.crop || null,
    renditionAssetId: null,
  }
}
